<template>
  <main-content>
    <div class="col-12">
      <!-- Service Status Overview -->
      <div class="card card-outline card-primary mb-4">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fas fa-server mr-2"></i>
            Streaming Services Control
          </h3>
        </div>
        <div class="card-body">
          <div class="row">
            <!-- Frontend Status -->
            <div class="col-md-6">
              <div class="info-box" :class="frontendRunning ? 'bg-success' : 'bg-secondary'">
                <span class="info-box-icon">
                  <i class="fas fa-broadcast-tower"></i>
                </span>
                <div class="info-box-content">
                  <span class="info-box-text">Frontend</span>
                  <span class="info-box-number">
                    {{ frontendRunning ? 'Running' : 'Stopped' }}
                  </span>
                  <span class="progress-description">
                    Streaming Server - Port {{ frontendPort }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Backend Status -->
            <div class="col-md-6">
              <div class="info-box" :class="backendRunning ? 'bg-success' : 'bg-secondary'">
                <span class="info-box-icon">
                  <i class="fas fa-cogs"></i>
                </span>
                <div class="info-box-content">
                  <span class="info-box-text">Backend</span>
                  <span class="info-box-number">
                    {{ backendRunning ? 'Running' : 'Stopped' }}
                  </span>
                  <span class="progress-description">
                    AutoDJ & Playlist Manager - Port {{ backendPort }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Combined Actions -->
      <div class="card card-outline card-info mb-4">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fas fa-play-circle mr-2"></i>
            Quick Actions (Both Services)
          </h3>
        </div>
        <div class="card-body">
          <div class="btn-group" role="group">
            <button
              :disabled="combinedStatus.restarting"
              @click="combinedAction('restart')"
              class="btn btn-info btn-lg mr-2"
            >
              <i class="fas fa-redo-alt mr-1"></i>
              {{ combinedStatus.restarting ? 'Restarting...' : 'Restart Both' }}
            </button>
            <button
              :disabled="combinedStatus.starting"
              @click="combinedAction('start')"
              class="btn btn-success btn-lg mr-2"
              v-if="!frontendRunning || !backendRunning"
            >
              <i class="fas fa-play mr-1"></i>
              {{ combinedStatus.starting ? 'Starting...' : 'Start Both' }}
            </button>
            <button
              :disabled="combinedStatus.stopping"
              @click="combinedAction('stop')"
              class="btn btn-danger btn-lg"
              v-if="frontendRunning || backendRunning"
            >
              <i class="fas fa-stop mr-1"></i>
              {{ combinedStatus.stopping ? 'Stopping...' : 'Stop Both' }}
            </button>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Frontend Control -->
        <div class="col-md-6">
          <div class="card card-outline card-success">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-broadcast-tower mr-2"></i>
                Frontend
                <span
                  class="badge badge-pill ml-2"
                  :class="frontendRunning ? 'badge-success' : 'badge-danger'"
                >
                  {{ frontendRunning ? 'Running' : 'Stopped' }}
                </span>
              </h3>
            </div>
            <div class="card-body">
              <p class="text-muted">
                The <strong>Frontend</strong> is the streaming server that broadcasts your audio to listeners. 
                It must be running for listeners to hear your stream.
              </p>
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td><strong>Port:</strong></td>
                    <td>{{ frontendPort }}</td>
                  </tr>
                  <tr>
                    <td><strong>Mount:</strong></td>
                    <td>{{ station.mount_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer">
              <div class="btn-group" role="group">
                <button
                  :disabled="frontendStatus.restarting || frontendStatus.processing"
                  @click="frontendAction('restart')"
                  class="btn btn-outline-info btn-sm mr-2"
                >
                  <i class="fas fa-redo-alt"></i>
                  {{ frontendStatus.restarting ? 'Restarting...' : 'Restart' }}
                </button>
                <button
                  :disabled="frontendStatus.processing || frontendStatus.restarting"
                  @click="frontendAction('start')"
                  class="btn btn-outline-success btn-sm mr-2"
                  v-if="!frontendRunning"
                >
                  <i class="fas fa-play-circle"></i>
                  {{ frontendStatus.processing ? 'Starting...' : 'Start' }}
                </button>
                <button
                  :disabled="frontendStatus.processing || frontendStatus.restarting"
                  @click="frontendAction('stop')"
                  class="btn btn-outline-danger btn-sm"
                  v-if="frontendRunning"
                >
                  <i class="fas fa-stop-circle"></i>
                  {{ frontendStatus.processing ? 'Stopping...' : 'Stop' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Backend Control -->
        <div class="col-md-6">
          <div class="card card-outline card-warning">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-cogs mr-2"></i>
                Backend
                <span
                  class="badge badge-pill ml-2"
                  :class="backendRunning ? 'badge-success' : 'badge-danger'"
                >
                  {{ backendRunning ? 'Running' : 'Stopped' }}
                </span>
              </h3>
            </div>
            <div class="card-body">
              <p class="text-muted">
                The <strong>Backend</strong> manages your playlists and AutoDJ. 
                Stop it when you want to stream live directly to the Frontend.
              </p>
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td><strong>Port:</strong></td>
                    <td>{{ backendPort }}</td>
                  </tr>
                  <tr>
                    <td><strong>Tracks:</strong></td>
                    <td>{{ station.number_of_tracks || 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer">
              <div class="btn-group" role="group">
                <button
                  :disabled="backendStatus.restarting || backendStatus.processing"
                  @click="backendAction('restart')"
                  class="btn btn-outline-info btn-sm mr-2"
                >
                  <i class="fas fa-redo-alt"></i>
                  {{ backendStatus.restarting ? 'Restarting...' : 'Restart' }}
                </button>
                <button
                  :disabled="backendStatus.processing || backendStatus.restarting"
                  @click="backendAction('start')"
                  class="btn btn-outline-success btn-sm mr-2"
                  v-if="!backendRunning"
                >
                  <i class="fas fa-play-circle"></i>
                  {{ backendStatus.processing ? 'Starting...' : 'Start' }}
                </button>
                <button
                  :disabled="backendStatus.processing || backendStatus.restarting"
                  @click="backendAction('stop')"
                  class="btn btn-outline-danger btn-sm"
                  v-if="backendRunning"
                >
                  <i class="fas fa-stop-circle"></i>
                  {{ backendStatus.processing ? 'Stopping...' : 'Stop' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Direct Frontend Streaming Instructions -->
      <div class="card card-outline card-danger mt-4">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fas fa-microphone-alt mr-2"></i>
            Stream Directly to Frontend (Skip Backend)
          </h3>
          <div class="card-tools">
            <button type="button" class="btn btn-tool" data-card-widget="collapse">
              <i class="fas fa-minus"></i>
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Important:</strong> To stream directly to Frontend without Backend interference, 
            you must <strong>stop the Backend</strong> first. The Frontend must remain running.
          </div>

          <div class="row">
            <div class="col-md-6">
              <h5 class="text-info">
                <i class="fas fa-info-circle mr-2"></i>
                Connection Details for Direct Streaming
              </h5>
              <table class="table table-bordered">
                <tbody>
                  <tr>
                    <td><strong>Server Address:</strong></td>
                    <td>
                      <span id="direct_server">{{ station.server_address }}</span>
                      <copy-to-clipboard-button target="#direct_server" hide-text></copy-to-clipboard-button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Port:</strong></td>
                    <td>
                      <span id="direct_port">{{ frontendPort }}</span>
                      <copy-to-clipboard-button target="#direct_port" hide-text></copy-to-clipboard-button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Mount Point:</strong></td>
                    <td>
                      <span id="direct_mount">{{ station.mount_name }}</span>
                      <copy-to-clipboard-button target="#direct_mount" hide-text></copy-to-clipboard-button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Username:</strong></td>
                    <td>
                      <span id="direct_user">{{ station.dj_username }}</span>
                      <copy-to-clipboard-button target="#direct_user" hide-text></copy-to-clipboard-button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Password:</strong></td>
                    <td>
                      <span id="direct_pass">{{ station.dj_password }}</span>
                      <copy-to-clipboard-button target="#direct_pass" hide-text></copy-to-clipboard-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="alert alert-info mt-3">
                <i class="fas fa-lightbulb mr-2"></i>
                <strong>Port Difference:</strong> 
                <ul class="mb-0 mt-2">
                  <li><strong>Frontend:</strong> Port starts with <code>2</code> (e.g., {{ frontendPort }})</li>
                  <li><strong>Backend:</strong> Port starts with <code>1</code> (e.g., {{ backendPort }})</li>
                </ul>
              </div>
            </div>

            <div class="col-md-6">
              <h5 class="text-success">
                <i class="fas fa-list-ol mr-2"></i>
                Step-by-Step Instructions
              </h5>
              <ol class="list-group list-group-numbered">
                <li class="list-group-item d-flex">
                  <div>
                    <strong>1. Stop the Backend</strong>
                    <p class="mb-0 text-muted small">
                      Click "Stop" on the Backend panel above. 
                      This frees up the mount point for your direct stream.
                    </p>
                  </div>
                </li>
                <li class="list-group-item d-flex">
                  <div>
                    <strong>2. Ensure Frontend is Running</strong>
                    <p class="mb-0 text-muted small">
                      The Frontend must be running to accept your stream.
                      Start it if it's stopped.
                    </p>
                  </div>
                </li>
                <li class="list-group-item d-flex">
                  <div>
                    <strong>3. Configure Your Streaming Software</strong>
                    <p class="mb-0 text-muted small">
                      Use BUTT, Mixxx, OBS, or any Icecast-compatible software.
                      Enter the connection details from the left panel.
                    </p>
                  </div>
                </li>
                <li class="list-group-item d-flex">
                  <div>
                    <strong>4. Start Streaming</strong>
                    <p class="mb-0 text-muted small">
                      Connect from your streaming software to go live!
                    </p>
                  </div>
                </li>
                <li class="list-group-item d-flex list-group-item-warning">
                  <div>
                    <strong>5. After You're Done</strong>
                    <p class="mb-0 text-muted small">
                      <i class="fas fa-exclamation-circle text-warning mr-1"></i>
                      Remember to <strong>start the Backend</strong> again to resume 
                      AutoDJ and playlist management.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <!-- Recommended Software -->
      <div class="card card-outline card-secondary mt-4">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fas fa-download mr-2"></i>
            Recommended Streaming Software
          </h3>
          <div class="card-tools">
            <button type="button" class="btn btn-tool" data-card-widget="collapse">
              <i class="fas fa-minus"></i>
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-4">
              <div class="card">
                <div class="card-body text-center">
                  <h5>BUTT</h5>
                  <p class="text-muted small">Broadcast Using This Tool - Simple and lightweight</p>
                  <a href="https://sourceforge.net/projects/butt/files/butt/" 
                     target="_blank" 
                     class="btn btn-outline-primary btn-sm">
                    <i class="fas fa-download mr-1"></i> Download
                  </a>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card">
                <div class="card-body text-center">
                  <h5>Mixxx</h5>
                  <p class="text-muted small">Professional DJ software with broadcasting</p>
                  <a href="https://mixxx.org/download/" 
                     target="_blank" 
                     class="btn btn-outline-primary btn-sm">
                    <i class="fas fa-download mr-1"></i> Download
                  </a>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card">
                <div class="card-body text-center">
                  <h5>OBS Studio</h5>
                  <p class="text-muted small">With Icecast plugin for audio streaming</p>
                  <a href="https://obsproject.com/download" 
                     target="_blank" 
                     class="btn btn-outline-primary btn-sm">
                    <i class="fas fa-download mr-1"></i> Download
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </main-content>
</template>

<script>
import CopyToClipboardButton from '@/components/common/CopyToClipboardButton';
import { SET_STATION_INFO } from "@/store/mutation-types";
import { SERVER_ACTION } from "@/store/action-types";

export default {
  name: 'StreamingControl',

  components: {
    CopyToClipboardButton
  },

  data() {
    return {
      frontendStatus: {
        processing: false,
        restarting: false
      },
      backendStatus: {
        processing: false,
        restarting: false
      },
      combinedStatus: {
        starting: false,
        stopping: false,
        restarting: false
      }
    };
  },

  computed: {
    frontendPort() {
      // Frontend port starts with 2
      const djPort = this.station.cluster_dj_port;
      if (djPort) {
        return '2' + String(djPort).substring(1);
      }
      return djPort;
    },
    backendPort() {
      // Backend port starts with 1
      return this.station.cluster_dj_port;
    },
    backendRunning() {
      return this.station_info.backend_running;
    },
    frontendRunning() {
      // Use frontend_running if available, otherwise fall back to onAir (server_running)
      return this.station_info.frontend_running !== undefined 
        ? this.station_info.frontend_running 
        : this.onAir;
    }
  },

  methods: {
    showToast(message, type = 'success') {
      const Toast = swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
      Toast.fire({
        icon: type,
        title: message
      });
    },

    async frontendAction(action) {
      const key = action === 'restart' ? 'restarting' : 'processing';
      // URL format: admin/services/frontend/{action}/{stationId} - same as dashboard
      const uri = `admin/services/frontend/${action}`;

      try {
        this.frontendStatus[key] = true;
        await this.$store.dispatch(SERVER_ACTION, uri);
        this.frontendStatus[key] = false;
        this.showToast(`Frontend ${action}ed successfully`, 'success');
      } catch (error) {
        this.frontendStatus[key] = false;
        this.showToast(`Failed to ${action} frontend`, 'error');
        console.error(error);
      }
    },

    async backendAction(action) {
      const key = action === 'restart' ? 'restarting' : 'processing';
      // URL format: admin/services/backend/{action}/{stationId} - same as dashboard
      const uri = `admin/services/backend/${action}`;

      try {
        this.backendStatus[key] = true;
        await this.$store.dispatch(SERVER_ACTION, uri);
        this.backendStatus[key] = false;
        this.showToast(`Backend ${action}ed successfully`, 'success');
      } catch (error) {
        this.backendStatus[key] = false;
        this.showToast(`Failed to ${action} backend`, 'error');
        console.error(error);
      }
    },

    async combinedAction(action) {
      const key = action === 'restart' ? 'restarting' : (action === 'start' ? 'starting' : 'stopping');

      try {
        this.combinedStatus[key] = true;

        // For combined actions, call both frontend and backend endpoints
        const frontendUri = `admin/services/frontend/${action}`;
        const backendUri = `admin/services/backend/${action}`;

        // Call both endpoints sequentially (frontend first, then backend)
        await this.$store.dispatch(SERVER_ACTION, frontendUri);

        // Small delay between starting services for stability
        if (action === 'start' || action === 'restart') {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        await this.$store.dispatch(SERVER_ACTION, backendUri);

        this.combinedStatus[key] = false;
        this.showToast(`Both services ${action}ed successfully`, 'success');
      } catch (error) {
        this.combinedStatus[key] = false;
        this.showToast(`Failed to ${action} services`, 'error');
        console.error(error);
      }
    }
  }
};
</script>

<style scoped>
.info-box {
  min-height: 100px;
}

.info-box-icon {
  font-size: 2rem;
}

.list-group-item {
  border-left: 3px solid #17a2b8;
}

.list-group-item-warning {
  border-left: 3px solid #ffc107;
}

.table-borderless td {
  border: none;
  padding: 0.25rem 0;
}

.card-outline.card-primary {
  border-top: 3px solid #007bff;
}

.card-outline.card-info {
  border-top: 3px solid #17a2b8;
}

.card-outline.card-success {
  border-top: 3px solid #28a745;
}

.card-outline.card-warning {
  border-top: 3px solid #ffc107;
}

.card-outline.card-danger {
  border-top: 3px solid #dc3545;
}

.card-outline.card-secondary {
  border-top: 3px solid #6c757d;
}
</style>
